import { useState } from "react"
import { Component, FieldErrorsObj, ParsedActionError } from "@/types"
import { User } from "@/schemas/User"
import { cx } from "@/utils/cx"
import { executeCaptcha } from "@/actions/captcha/executeCaptcha"
import { SignInKeys, validateSignIn } from "@/actions/_archive/auth/signIn/signIn.interface"
import { useSearchParams } from "next/navigation"
import { useSignIn } from "@/hooks/auth/useSignIn"

import { Button } from "@/ui/atoms/controls/Button"
import { FieldGroup } from "@/ui/atoms/form/FieldGroup"
import { Form } from "@/ui/atoms/form/Form"
import { FormFields } from "@/ui/atoms/form/FormFields"
import { FormLinksWrapper } from "@/ui/atoms/form/FormLinksWrapper"
import { NotificationCard } from "@/ui/blocks/notifications/NotificationCard"
import { PasswordField } from "@/ui/atoms/form/PasswordField"
import { SplashCard } from "@/ui/atoms/layout/SplashCard"
import { Stack } from "@/ui/atoms/layout/Stack"
import { TextField } from "@/ui/atoms/form/TextField"
import { TextLink } from "@/ui/atoms/controls/TextLink"
import { Title } from "@/ui/atoms/type/Title"

import { errors } from "./LegacySignInForm.errors"
import { useCourseStore } from "@/store/courseStoreProvider"
import { Pill } from "@/ui/atoms/data/Pill"

export const COMPONENT_NAME = "signInForm"

export const presets = {}

export interface Props extends Component {
  onSuccess: (newCurrentUser: User, redirectTo?: string) => void
}

export function LegacySignInForm({ onSuccess, className, testId = COMPONENT_NAME }: Props) {
  const { CAPTCHA_SITE_KEY } = useCourseStore((s) => s.envVars)
  const searchParams = useSearchParams()
  const reason = searchParams.get("r")
  const redirectTo = searchParams.get("to")
  const signIn = useSignIn()

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")

  const [formStatus, setFormStatus] = useState<"ready" | "submitting">("ready")
  const [formError, setFormError] = useState<null | ParsedActionError>(null)
  const [fieldErrors, setFieldErrors] = useState<null | FieldErrorsObj<SignInKeys>>(null)

  async function handleSubmit() {
    setFieldErrors(null)
    setFormStatus("submitting")

    const [recaptchaResponse, recaptchaError] = await executeCaptcha("signIn", CAPTCHA_SITE_KEY)
    if (recaptchaError) {
      setFormError(errors["CAPTCHA_FAILED"])
      setFormStatus("ready")
      return
    }

    const [validatedData, validationFieldErrors] = validateSignIn({
      email,
      password,
      recaptchaResponse,
    })

    if (validationFieldErrors) {
      setFormError(errors["INVALID_INPUT"])
      setFieldErrors(validationFieldErrors)
      setFormStatus("ready")
      return
    }

    const [newCurrentUser, signInErrorCode] = await signIn(validatedData)

    if (signInErrorCode) {
      setFormError(errors[signInErrorCode])
      setFormStatus("ready")
      return
    }
    onSuccess(newCurrentUser, !!redirectTo ? decodeURIComponent(redirectTo) : undefined)
  }

  return (
    <SplashCard className={cx(`w-full max-w-screen-form`, className)} testId={testId}>
      <Form name="Sign in" onSubmit={handleSubmit}>
        <header>
          <Stack className="gap-10 md:gap-8">
            <div className="flex justify-center items-center gap-2">
              <Title size="lg" className="text-center">
                Sign in
              </Title>
              <Pill>Legacy</Pill>
            </div>
            <NotificationCard
              variant="warning"
              title="Not for SANS account users"
              text="Most users should now sign in via SANS. Signing in with your email and password will only work for certain legacy accounts created before September 25, 2025."
              links={[
                {
                  label: "Sign in with SANS",
                  href: "/sign-in",
                },
              ]}
              className="border border-border-form border-l-0"
            />
            {reason === "NOT_LOGGED_IN" && (
              <NotificationCard
                variant="warning"
                title="Sign in to continue"
                text="You need to be signed in to access this page"
                links={[]}
                className="border border-border-form border-l-0"
              />
            )}

            {reason === "SESSION_EXPIRED" && (
              <NotificationCard
                variant="warning"
                title="Sign in to continue"
                text="Your session has expired, please sign in again to continue"
                links={[]}
                className="border border-border-form border-l-0"
              />
            )}
          </Stack>
        </header>
        <main className="flex flex-col gap-8">
          <FormFields>
            <FieldGroup>
              <TextField
                name="email"
                error={fieldErrors?.email?.[0]}
                type="email"
                label="Email"
                testId="emailField"
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
              />
            </FieldGroup>
            <FieldGroup>
              <PasswordField
                name="password"
                error={fieldErrors?.password?.[0]}
                label="Password"
                testId="passwordField"
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
              />
            </FieldGroup>
          </FormFields>

          {formError && (
            <Stack className="gap-3">
              <NotificationCard
                variant="failure"
                title={formError.title}
                text={formError.message}
                links={formError.links}
                className="border border-border-form border-l-0"
                testId="formErrorCard"
              />
            </Stack>
          )}
          <Button
            variant="primary"
            label="Sign in"
            width="full"
            type="submit"
            testId="submitButton"
            state={formStatus === "submitting" ? "loading" : "ready"}
          />
        </main>
        <footer className="">
          <FormLinksWrapper>
            <TextLink href="/legacy/forgot-password" testId="forgotPasswordLink" style="bold">
              Trouble signing in (legacy)
            </TextLink>
          </FormLinksWrapper>
        </footer>
      </Form>
    </SplashCard>
  )
}
